<template>
  <div>
    <v-flex class="pb-16">
      <text-field
        v-model.trim="form.replyTo"
        :validator="vs.form.replyTo"
        :label="adminText('workflowsAll.reply_to_e_mail_a8e268a', 'Reply-to e-mail')"
      />
    </v-flex>
    <v-flex class="pb-16">
      <select-field
        v-model="form.recipients"
        :validator="vs.form.recipients"
        :items="workflowUi(recipients)"
        :disabled="recipients.length === 1"
        :label="adminText('workflowsAll.recipients_faf993d', 'Recipients*')"
      />
    </v-flex>
    <v-flex v-if="ticketFilterEnabled && ticketTypes.length" class="pb-16">
      <v-autocomplete
        ref="ticketTypeSelect"
        v-model="form.orderItemIds"
        v-click-outside="handleClickOutsideTicketTypes"
        :items="workflowUi(ticketTypeOptions)"
        :menu-props="{ contentClass: 'email-create-autocomplete-content' }"
        item-value="id"
        item-text="label"
        :label="adminText('workflowsAll.type_of_ticket_3968f24', 'Type of ticket')"
        multiple
        small-chips
        deletable-chips
        box
        class="email-create-autocomplete"
        @keydown.enter.native.stop.prevent
        @keyup.enter.native.stop.prevent
      />
    </v-flex>
    <v-flex v-if="showSpecificRecipientSelect" class="pb-16">
      <v-autocomplete
        v-if="form.recipients == 'Specific categories'"
        ref="catSelect"
        v-model="form.categories"
        v-click-outside="handleClickOutsideCategory"
        :items="workflowUi(categories)"
        :menu-props="{ contentClass: 'email-create-autocomplete-content' }"
        item-value="id"
        item-text="name"
        :label="adminText('workflowsAll.categories_6ccb600', 'Categories')"
        multiple
        small-chips
        deletable-chips
        box
        class="email-create-autocomplete"
        @focus="form.attendees = []"
        @keydown.enter.native.stop.prevent
        @keyup.enter.native.stop.prevent
      />
      <v-autocomplete
        v-if="form.recipients == 'Specific attendees'"
        ref="attSelect"
        v-model="form.attendees"
        v-click-outside="handleClickOutsideAttendees"
        :items="workflowUi(filteredAttendees)"
        :menu-props="{ contentClass: 'email-create-autocomplete-content' }"
        item-value="id"
        item-text="name"
        :label="adminText('workflowsAll.attendees_47d7867', 'Attendees*')"
        multiple
        small-chips
        deletable-chips
        box
        class="email-create-autocomplete"
        @focus="form.categories = []"
        @keydown.enter.native.stop.prevent
        @keyup.enter.native.stop.prevent
      />
    </v-flex>
    <v-flex class="pb-16">
      <text-field v-model.trim="form.subject" :validator="vs.form.subject" :label="adminText('workflowsAll.subject_8d183db', 'Subject')" />
    </v-flex>
    <v-flex class="pb-16">
      <text-editor-field
        v-model.trim="form.content"
        :validator="vs.form.content"
        :label="adminText('workflowsAll.content_4f9be05', 'Content')"
        height="390"
        :image-and-code-block-enabled="imageAndCodeBlockEnabled"
      />
    </v-flex>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import {
  GET_EMAIL_RECIPIENT_PICKER_ACCREDITATIONS,
  GET_EVENT_CATEGORIES,
} from '../../graphql/event.graphql'
import { GET_ORDER_ITEM_TICKET_OPTIONS } from '../../graphql/orderItem.graphql'

export default {
  mixins: [WorkflowDisplay],
  name: 'create-email-form',
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    eventId: {
      default: null,
      type: String,
    },
    imageAndCodeBlockEnabled: {
      type: Boolean,
      default: false,
    },
    ticketFilterEnabled: {
      type: Boolean,
      default: false,
    },
    recipients: {
      type: Array,
      default: () => ['All attendees', 'Specific categories', 'Specific attendees'],
    },
  },
  data() {
    return {
      form: [],
      categories: [],
      attendees: [],
      initAttendees: [],
      initCategories: [],
      ticketTypes: [],
    }
  },
  computed: {
    showSpecificRecipientSelect() {
      return ['Specific categories', 'Specific attendees'].includes(this.form.recipients)
    },
    selectedOrderItemIds() {
      return (this.form.orderItemIds || []).map(orderItemId => orderItemId.toString())
    },
    ticketTypeOptions() {
      return this.ticketTypes.map(ticketType => {
        const recipientCount = this.getTicketTypeRecipientCount(ticketType.id)

        return {
          ...ticketType,
          label: `${ticketType.name} (${recipientCount})`,
        }
      })
    },
    filteredAttendees() {
      if (!this.selectedOrderItemIds.length) {
        return this.attendees
      }

      return this.attendees.filter(attendee => {
        const attendeeOrderItemIds = this.getAttendeeOrderItemIds(attendee)

        return attendeeOrderItemIds.some(orderItemId => this.selectedOrderItemIds.includes(orderItemId))
      })
    },
  },
  watch: {
    'form.recipients'(recipientType) {
      if (recipientType === 'All attendees') {
        this.form.categories = []
        this.form.attendees = []
      } else if (recipientType === 'Specific categories') {
        this.form.attendees = []
      } else if (recipientType === 'Specific attendees') {
        this.form.categories = []
      }
    },
    'form.orderItemIds'() {
      this.removeAttendeesWithoutSelectedTicket()
    },
    form: {
      handler(val) {
        if (val.type === 'Automated' && this.recipients.length > 1) {
          if (this.recipients.indexOf('Specific attendees') >= 0) {
            this.recipients.splice(this.recipients.indexOf('Specific attendees'), 1)
          }
        }
        this.$emit('values', val)
      },
      deep: true,
    },
  },
  mounted() {
    const { attendees, categories, meta, ...rest } = this.initialValues
    this.form = {
      ...rest,
      categories: [],
      attendees: [],
      orderItemIds: meta && meta.orderItemIds ? meta.orderItemIds : [],
    }
    this.initAttendees = attendees || []
    this.initCategories = categories || []
  },
  methods: {
    handleClickOutsideAttendees() {
      this.$refs.attSelect.isMenuActive = false
    },
    handleClickOutsideCategory() {
      this.$refs.catSelect.isMenuActive = false
    },
    handleClickOutsideTicketTypes() {
      if (this.$refs.ticketTypeSelect) {
        this.$refs.ticketTypeSelect.isMenuActive = false
      }
    },
    removeAttendeesWithoutSelectedTicket() {
      if (!this.selectedOrderItemIds.length || !this.form.attendees || !this.form.attendees.length) {
        return
      }

      const filteredAttendeeIds = this.filteredAttendees.map(attendee => attendee.id.toString())
      this.form.attendees = this.form.attendees.filter(attendee => {
        const attendeeId = attendee && typeof attendee === 'object' ? attendee.id : attendee

        return filteredAttendeeIds.includes(attendeeId.toString())
      })
    },
    getAttendeeOrderItemIds(attendee) {
      return (attendee.orderItemIds || []).map(orderItemId => orderItemId.toString())
    },
    getTicketTypeRecipientCount(orderItemId) {
      const normalizedOrderItemId = orderItemId.toString()

      return this.attendees.filter(attendee =>
        this.getAttendeeOrderItemIds(attendee).includes(normalizedOrderItemId),
      ).length
    },
  },
  apollo: {
    getEventCategories: {
      query: GET_EVENT_CATEGORIES,
      variables() {
        return {
          id: this.eventId,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getEventCategories: { categories },
          } = data
          this.categories = categories
          if (this.categories.length) {
            this.categories.map(categorie => {
              if (this.initCategories.includes(categorie.id.toString())) {
                this.form.categories.push(categorie)
              }
            })
          } else {
            if (this.recipients.indexOf('Specific attendees') >= 0) {
              this.recipients.splice(this.recipients.indexOf('Specific categories'), 1)
            }
          }
        }
      },
    },
    getEmailRecipientPickerAccreditations: {
      query: GET_EMAIL_RECIPIENT_PICKER_ACCREDITATIONS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getEmailRecipientPickerAccreditations: { attendees },
          } = data
          this.attendees = attendees.map(function (attendee) {
            return {
              name:
                (attendee.fields['First name'] || attendee.fields['Ime']) +
                ' ' +
                (attendee.fields['Last name'] || attendee.fields['Prezime']),
              ...attendee,
            }
          })
          if (this.initAttendees.length) {
            this.attendees.map(attendee => {
              if (this.initAttendees.includes(attendee.id.toString())) {
                this.form.attendees.push(attendee)
              }
            })
          }
          if (!this.attendees.length && this.recipients.length > 1) {
            if (this.recipients.indexOf('Specific attendees') >= 0) {
              this.recipients.splice(this.recipients.indexOf('Specific attendees'), 1)
            }
          }
        }
      },
    },
    getOrderItems: {
      query: GET_ORDER_ITEM_TICKET_OPTIONS,
      skip() {
        return !this.ticketFilterEnabled || !this.eventId
      },
      variables() {
        return {
          eventId: this.eventId,
          pagination: { page: 1, per_page: -1 },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getOrderItems: { items },
          } = data
          this.ticketTypes = items.filter(item => !item.type || item.type === 'ticket')
        }
      },
    },
  },
}
</script>

<style>
.email-create-autocomplete.v-text-field--box .v-input__slot {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  min-height: 56px;
}

.email-create-autocomplete.v-text-field--box .v-input__slot::before,
.email-create-autocomplete.v-text-field--box .v-input__slot::after {
  display: none;
}

.email-create-autocomplete .v-select__slot,
.email-create-autocomplete .v-select__selections {
  min-height: 56px;
}

.email-create-autocomplete .v-label {
  color: #666666;
  font-size: 16px;
  letter-spacing: 0.5px;
}

.email-create-autocomplete.v-input--is-focused .v-input__slot {
  border-color: #0277bd;
}

.email-create-autocomplete-content .v-list__tile {
  min-height: 40px;
}

.email-create-autocomplete-content .v-list__tile__title {
  white-space: normal;
}
</style>
